import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ChevronLeft, ChevronRight, MapPin, Phone, Mail, Instagram, Facebook, MessageCircle, Play, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  heroData,
  tattooCategories,
  tattooStyles,
  schoolData,
  studentWorks,
  aboutData,
  locationData,
  contactData,
  navigationLinks
} from '../data/mock';

// Header Component
const Header = ({ isSchoolMode, setIsSchoolMode }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const toggleSchoolMode = () => {
    const newMode = !isSchoolMode;
    setIsSchoolMode(newMode);
    window.scrollTo(0, 0);
    // Update URL
    if (newMode) {
      navigate('/escola');
    } else {
      navigate('/');
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-black/95 backdrop-blur-sm' : 'bg-transparent'
    }`}>
      <div className="container-custom flex items-center justify-between py-4">
        {/* Desktop Navigation */}
        {!isSchoolMode && (
          <nav className="hidden lg:flex items-center gap-8 flex-1 justify-center" translate="no">
            {navigationLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.href)}
                className="text-white/80 hover:text-white text-sm uppercase tracking-widest transition-colors duration-300 notranslate"
              >
                {link.label}
              </button>
            ))}
          </nav>
        )}

        {/* Toggle School Mode Button - Enhanced */}
        <button
          onClick={toggleSchoolMode}
          className="group relative px-6 py-3 rounded-full text-sm font-semibold transition-all duration-500 transform hover:scale-105 overflow-hidden bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 text-black shadow-lg hover:shadow-amber-500/50"
          data-testid="toggle-mode-btn"
        >
          {/* Animated background shine effect */}
          <span className="absolute inset-0 w-full h-full transition-all duration-500 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full" style={{ transitionDuration: '700ms' }}></span>
          
          {/* Button content */}
          <span className="relative flex items-center gap-2">
            {isSchoolMode ? (
              <>
                <span className="text-lg transform group-hover:rotate-12 transition-transform duration-300">✒️</span>
                <span className="hidden sm:inline">Voltar ao Ubiratan Tattoo Studio</span>
                <span className="sm:hidden">Voltar ao Studio</span>
                <svg className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </>
            ) : (
              <>
                <span className="text-lg transform group-hover:scale-110 transition-transform duration-300">🎓</span>
                <span className="hidden sm:inline">Ubiratan School</span>
                <span className="sm:hidden">School</span>
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </>
            )}
          </span>
        </button>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="lg:hidden p-2 text-white"
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {!isSchoolMode && (
        <div className={`lg:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-sm transition-all duration-300 overflow-hidden ${isMenuOpen ? 'max-h-screen' : 'max-h-0'}`}>
          <nav className="container-custom py-6 flex flex-col gap-4" translate="no">
            {navigationLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.href)}
                className="text-white/80 hover:text-white text-left py-2 uppercase tracking-widest transition-colors duration-300 notranslate"
              >
                {link.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section Component
const HeroSection = () => {
  const [currentTattoo, setCurrentTattoo] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTattoo((prev) => (prev + 1) % heroData.tattooWorks.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section id="home" className="relative h-screen w-full overflow-hidden bg-black">
      {/* Mobile Layout - Tattoos carousel only */}
      <div className="lg:hidden absolute inset-0">
        {heroData.tattooWorks.map((img, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentTattoo ? 'opacity-100' : 'opacity-0'}`}
          >
            <img
              src={img}
              alt={`Trabalho ${index + 1}`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40" />
          </div>
        ))}
      </div>

      {/* Desktop Layout - Two columns */}
      <div className="hidden lg:flex absolute inset-0">
        {/* Lado Esquerdo - Foto do Ubiratan */}
        <div className="relative w-1/2 h-full">
          <img
            src={heroData.image}
            alt={heroData.title}
            className="w-full h-full object-cover object-top brightness-110"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black" />
        </div>

        {/* Lado Direito - Carrossel de Tattoos */}
        <div className="absolute right-0 top-0 w-1/2 h-full">
          {heroData.tattooWorks.map((img, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${index === currentTattoo ? 'opacity-100' : 'opacity-0'}`}
            >
              <img
                src={img}
                alt={`Trabalho ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30" />
            </div>
          ))}
        </div>
      </div>

      {/* Gradiente inferior */}
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-black to-transparent z-10" />

      {/* Content - Texto no fundo */}
      <div className="absolute bottom-8 md:bottom-12 left-0 right-0 px-6 md:px-16 z-20">
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-bold mb-3 leading-tight drop-shadow-lg">
            {heroData.title}
          </h1>
          <p className="text-white text-base md:text-base max-w-2xl mx-auto italic font-light leading-relaxed drop-shadow-md">
            "{heroData.subtitle}"
          </p>
        </div>
      </div>
    </section>
  );
};

// Tattoo Gallery Grid Component
const TattooCategories = () => {
  const [selectedTattoo, setSelectedTattoo] = useState(null);
  const [showMore, setShowMore] = useState(false);

  const openModal = (item) => {
    if (item.instagramUrl) {
      setSelectedTattoo(item);
    }
  };

  const closeModal = () => {
    setSelectedTattoo(null);
  };

  // Extrair o ID do post do Instagram para embed
  const getInstagramEmbedUrl = (url) => {
    if (!url) return '';
    // Garante que a URL termina com / antes de adicionar embed
    const cleanUrl = url.endsWith('/') ? url : `${url}/`;
    return `${cleanUrl}embed/captioned/`;
  };

  // Separar as primeiras 6 tattoos e as adicionais
  const mainTattoos = tattooCategories.slice(0, 6);
  const moreTattoos = tattooCategories.slice(6);

  return (
    <section id="estilos" className="py-24 md:py-32 bg-black">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-serif text-3xl md:text-5xl text-white">Nossos Trabalhos</h2>
        </div>

        {/* Main Gallery Grid - First 6 */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {mainTattoos.map((item) => (
            <div
              key={item.id}
              className="group relative overflow-hidden cursor-pointer bg-gray-900"
              onClick={() => openModal(item)}
            >
              {/* Image */}
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  src={item.image}
                  alt={`Tatuagem ${item.id}`}
                  className="w-full h-full object-cover scale-[1.6] group-hover:scale-[1.7] transition-transform duration-700"
                />
              </div>

              {/* Overlay with button - only visible on hover */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-500 flex flex-col justify-end p-6 opacity-0 group-hover:opacity-100">
                {item.instagramUrl && (
                  <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal(item);
                      }}
                      className="bg-white text-black hover:bg-white/90 px-6 py-3 text-xs uppercase tracking-widest rounded-none"
                    >
                      Ver Mais
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Ver Mais Button */}
        {moreTattoos.length > 0 && !showMore && (
          <div className="text-center mt-12">
            <Button
              onClick={() => setShowMore(true)}
              className="bg-white text-black hover:bg-white/90 px-8 py-6 text-sm uppercase tracking-widest rounded-none"
            >
              Ver Mais
            </Button>
          </div>
        )}

        {/* Additional Tattoos Grid */}
        {showMore && moreTattoos.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mt-6">
            {moreTattoos.map((item) => (
              <div
                key={item.id}
                className="group relative overflow-hidden cursor-pointer bg-gray-900"
                onClick={() => openModal(item)}
              >
                {/* Image */}
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={item.image}
                    alt={`Tatuagem ${item.id}`}
                    className="w-full h-full object-cover scale-[1.6] group-hover:scale-[1.7] transition-transform duration-700"
                  />
                </div>

                {/* Overlay with button - only visible on hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-500 flex flex-col justify-end p-6 opacity-0 group-hover:opacity-100">
                  {item.instagramUrl && (
                    <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          openModal(item);
                        }}
                        className="bg-white text-black hover:bg-white/90 px-6 py-3 text-xs uppercase tracking-widest rounded-none"
                      >
                        Ver Mais
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedTattoo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90" onClick={closeModal}>
          <div 
            className="relative bg-gray-900 w-full max-w-5xl max-h-[90vh] overflow-y-auto md:overflow-hidden flex flex-col md:flex-row"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 text-white/60 hover:text-white transition-colors bg-black/50 rounded-full p-1"
              aria-label="Fechar"
            >
              <X size={28} />
            </button>

            {/* Instagram Video Embed */}
            <div className="w-full md:w-1/2 bg-black flex items-center justify-center min-h-[350px] md:min-h-[600px] flex-shrink-0">
              <iframe
                src={getInstagramEmbedUrl(selectedTattoo.instagramUrl)}
                width="100%"
                height="100%"
                frameBorder="0"
                scrolling="no"
                allowTransparency="true"
                allowFullScreen="true"
                className="w-full h-full min-h-[350px] md:min-h-[600px]"
                title="Instagram Video"
              />
            </div>

            {/* Content Side */}
            <div className="w-full md:w-1/2 p-6 md:p-12 flex flex-col justify-center flex-shrink-0">
              <h2 className="font-serif text-2xl md:text-4xl text-white mb-4 md:mb-6">
                {selectedTattoo.title}
              </h2>
              <p className="text-white/70 leading-relaxed mb-6 md:mb-8 text-sm md:text-base">
                {selectedTattoo.description}
              </p>
              <Button
                onClick={() => window.open(`https://api.whatsapp.com/send?phone=${contactData.whatsapp}&text=${encodeURIComponent('Olá! Gostaria de um orçamento para tatuagem.')}`, '_blank')}
                className="bg-white text-black hover:bg-white/90 px-6 md:px-8 py-4 text-sm uppercase tracking-widest rounded-none w-fit"
              >
                Solicitar Orçamento
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Styles Carousel Component
const StylesCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = useCallback(() => {
    if (tattooStyles.length > 0) {
      setCurrentIndex((prev) => (prev + 1) % tattooStyles.length);
    }
  }, []);

  const prevSlide = () => {
    if (tattooStyles.length > 0) {
      setCurrentIndex((prev) => (prev - 1 + tattooStyles.length) % tattooStyles.length);
    }
  };

  useEffect(() => {
    if (tattooStyles.length > 0) {
      const timer = setInterval(nextSlide, 4000);
      return () => clearInterval(timer);
    }
  }, [nextSlide]);

  if (tattooStyles.length === 0) return null;

  return (
    <section className="py-16 md:py-24 bg-black">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">Outros Estilos</h2>
          <p className="text-white/60">Conheça mais trabalhos do nosso estúdio</p>
        </div>

        {/* Carousel */}
        <div className="relative max-w-4xl mx-auto">
          {/* Main Image */}
          <div className="relative h-[500px] md:h-[600px] overflow-hidden">
            {tattooStyles.map((style, index) => (
              <div
                key={style.id}
                className={`absolute inset-0 transition-all duration-700 ${
                  index === currentIndex 
                    ? 'opacity-100 scale-100' 
                    : 'opacity-0 scale-105'
                }`}
              >
                <img
                  src={style.image}
                  alt={style.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                  <h3 className="font-serif text-2xl md:text-4xl text-white mb-4">
                    {style.name}
                  </h3>
                  <Button
                    onClick={() => window.open(`https://api.whatsapp.com/send?phone=${contactData.whatsapp}&text=${encodeURIComponent(style.whatsappMessage)}`, '_blank')}
                    className="bg-white text-black hover:bg-white/90 px-6 py-3 text-sm uppercase tracking-widest rounded-none"
                  >
                    Quero esse estilo
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors p-2 z-10"
            aria-label="Estilo anterior"
          >
            <ChevronLeft size={40} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors p-2 z-10"
            aria-label="Próximo estilo"
          >
            <ChevronRight size={40} />
          </button>
        </div>
      </div>
    </section>
  );
};

// Tattoo School Section Component
const TattooSchool = () => {
  const [currentWork, setCurrentWork] = useState(0);

  const nextWork = useCallback(() => {
    setCurrentWork((prev) => (prev + 1) % studentWorks.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextWork, 3000);
    return () => clearInterval(timer);
  }, [nextWork]);

  return (
    <section id="escola" className="py-24 md:py-32 bg-black">
      <div className="container-custom">
        {/* Main Layout */}
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left - Carousel rotating like banner */}
          <div className="w-full lg:w-1/2">
            <div className="relative h-[400px] md:h-[500px] lg:h-[600px]">
              {studentWorks.map((work, index) => (
                <div
                  key={work.id}
                  className={`absolute inset-0 flex items-center justify-center transition-opacity duration-1000 ${
                    index === currentWork ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={work.image}
                    alt={`Trabalho do aluno ${index + 1}`}
                    className="max-w-full max-h-full object-contain rounded-lg"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Right - Text Content */}
          <div className="w-full lg:w-1/2 text-center lg:text-left">
            <span className="text-white/50 uppercase tracking-widest text-sm mb-4 block">
              Aprenda com os melhores
            </span>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-6 leading-tight">
              {schoolData.title}
            </h2>
            <p className="text-2xl md:text-3xl text-white/80 font-light mb-6">
              {schoolData.subtitle}
            </p>
            <p className="text-white/60 leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0">
              {schoolData.description}
            </p>
            
            <h3 className="font-serif text-xl text-white/80 mb-6">
              Resultados dos Nossos Alunos
            </h3>

            <Button
              onClick={() => window.open(`https://api.whatsapp.com/send?phone=5511934633755&text=${encodeURIComponent("Olá! Gostaria de saber mais sobre o curso da Ubiratan School.")}`, '_blank')}
              className="bg-white text-black hover:bg-white/90 px-8 py-6 text-sm uppercase tracking-widest rounded-none"
            >
              Comece Sua Jornada
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section Component
const AboutSection = () => {
  return (
    <section id="sobre" className="py-24 md:py-32 bg-black">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={aboutData.image}
                alt="Ubiratan Amorim"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Decorative Border */}
            <div className="absolute -bottom-4 -right-4 w-full h-full border border-white/20 -z-10 hidden lg:block" />
          </div>

          {/* Content */}
          <div className="text-center lg:text-left">
            <p className="text-white/50 uppercase tracking-widest text-sm mb-4">
              {aboutData.title}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-8">
              {aboutData.subtitle}
            </h2>
            <div className="space-y-6">
              {aboutData.description.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-white/70 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="mt-10">
              <Button
                onClick={() => window.open(`https://api.whatsapp.com/send?phone=${contactData.whatsapp}&text=${encodeURIComponent('Olá! Gostaria de agendar uma consulta.')}`, '_blank')}
                className="bg-white text-black hover:bg-white/90 px-8 py-6 text-sm uppercase tracking-widest rounded-none"
              >
                Agendar Consulta
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Location Section Component
const LocationSection = () => {
  return (
    <section id="localizacao" className="py-24 md:py-32 bg-black">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-3xl md:text-5xl text-white mb-4">Localização</h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            Venha nos visitar e conhecer nosso espaço
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Address Info */}
          <div className="space-y-8 text-center lg:text-left">
            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-4">
              <div className="p-3 bg-white">
                <MapPin className="text-black" size={24} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-white mb-2">Endereço</h3>
                <p className="text-white/60">{locationData.address}</p>
                <p className="text-white/60">{locationData.city}</p>
                <p className="text-white/60">{locationData.postalCode}</p>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-4">
              <div className="p-3 bg-white">
                <Phone className="text-black" size={24} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-white mb-2">Telefone</h3>
                <p className="text-white/60">{contactData.phone}</p>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-4">
              <div className="p-3 bg-white">
                <Mail className="text-black" size={24} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-white mb-2">E-mail</h3>
                <p className="text-white/60">{contactData.email}</p>
              </div>
            </div>

            <div className="flex justify-center lg:justify-start">
              <Button
                onClick={() => window.open(`https://www.google.com/maps/search/${encodeURIComponent(locationData.fullAddress)}`, '_blank')}
                className="bg-white text-black hover:bg-white/90 px-8 py-6 text-sm uppercase tracking-widest rounded-none mt-4"
              >
                <ExternalLink size={18} className="mr-2" />
                Abrir no Google Maps
              </Button>
            </div>
          </div>

          {/* Map */}
          <div className="h-[400px] lg:h-[500px] overflow-hidden">
            <iframe
              src={locationData.mapEmbedUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localização do Estúdio"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Tour 360 Section Component
const Tour360Section = () => {
  return (
    <section id="tour" className="py-24 md:py-32 bg-black">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-3xl md:text-5xl text-white mb-4">Tour Virtual 360°</h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            Conheça nosso espaço antes mesmo de nos visitar
          </p>
        </div>

        {/* Google Maps 360 Tour */}
        <div className="relative max-w-5xl mx-auto overflow-hidden flex justify-center">
          <iframe 
            src="https://www.google.com/maps/embed?pb=!4v1767975707276!6m8!1m7!1sCAoSF0NJSE0wb2dLRUlDQWdJQzloUFBCaGdF!2m2!1d-23.55610781737393!2d-46.56317590472408!3f336.73!4f-3.1899999999999977!5f0.7820865974627469" 
            style={{ border: 0 }}
            allowFullScreen="" 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade"
            title="Tour Virtual 360° do Estúdio"
            className="w-full h-[450px] md:w-[800px] md:h-[600px]"
          />
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer id="contato" className="py-16 md:py-20 bg-black border-t border-gray-800">
      <div className="container-custom">
        <div className="grid md:grid-cols-3 gap-12 md:gap-8">
          {/* Logo & Description */}
          <div>
            <h3 className="font-serif text-2xl md:text-3xl text-white mb-4">Ubiratan Amorim</h3>
            <p className="text-white/60 mb-6">
              Arte, expressão e técnica. Transformando ideias em arte permanente desde 2008.
            </p>
            {/* Social Links */}
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/ubiratantattoostudio/"
                target="_blank"
                rel="noopener"
                className="p-3 bg-white text-black hover:bg-white/80 transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://www.facebook.com/UbiratanTattooStudio"
                target="_blank"
                rel="noopener"
                className="p-3 bg-white text-black hover:bg-white/80 transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a
                href={`https://api.whatsapp.com/send?phone=${contactData.whatsapp}`}
                target="_blank"
                rel="noopener"
                className="p-3 bg-white text-black hover:bg-white/80 transition-colors"
                aria-label="WhatsApp"
              >
                <MessageCircle size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg text-white mb-4">Links Rápidos</h4>
            <nav className="flex flex-col gap-3">
              {navigationLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.href}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-serif text-lg text-white mb-4">Contato</h4>
            <div className="space-y-3">
              <p className="text-white/60 flex items-center gap-2">
                <Phone size={16} />
                {contactData.phone}
              </p>
              <p className="text-white/60 flex items-center gap-2">
                <Mail size={16} />
                {contactData.email}
              </p>
              <p className="text-white/60 flex items-center gap-2">
                <MapPin size={16} />
                {locationData.city}
              </p>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-16 pt-8 border-t border-gray-800 text-center">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} Ubiratan Amorim. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp Button Component
const FloatingWhatsApp = ({ isSchoolMode }) => {
  const whatsappNumber = isSchoolMode ? "5511934633755" : contactData.whatsapp;
  const message = encodeURIComponent(isSchoolMode 
    ? "Olá! Gostaria de saber mais sobre o curso da Ubiratan School." 
    : "Olá! Gostaria de agendar uma consulta.");
  return (
    <a
      href={`https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${message}`}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group bg-green-500 hover:bg-green-600 text-white"
      aria-label="Contato via WhatsApp"
      data-testid="floating-whatsapp-btn"
    >
      <MessageCircle size={24} className="group-hover:animate-pulse" />
      <span className="font-medium text-sm hidden sm:inline">
        {isSchoolMode ? 'Fale Conosco' : 'Agendar Consulta'}
      </span>
    </a>
  );
};

// ============================================
// UBIRATAN SCHOOL SECTION - MODO ESCOLA
// ============================================

// School Hero Section
const SchoolHero = () => {
  const [currentImage, setCurrentImage] = useState(0);
  
  // Carrossel de trabalhos dos alunos (lado direito)
  const studentWorksImages = [
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/juuqm99t_ChatGPT%20Image%2022%20de%20jan.%20de%202026%2C%2005_44_50.png",
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/w9mxp982_ChatGPT%20Image%2022%20de%20jan.%20de%202026%2C%2005_41_27.png",
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/lnmocvve_ChatGPT%20Image%2022%20de%20jan.%20de%202026%2C%2005_40_18.png",
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/m4jh4rt0_2.jpeg",
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/2h6fo7uo_3.jpeg",
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/5lkegrd9_4.jpeg",
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/2p8i12nk_11.jpeg",
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/pz9a8wlp_111.jpeg",
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/c7m6cp80_22222222.jpeg"
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % studentWorksImages.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [studentWorksImages.length]);

  return (
    <section className="relative min-h-screen w-full overflow-hidden bg-black" data-testid="school-hero">
      {/* Layout com duas colunas */}
      <div className="absolute inset-0 flex flex-col-reverse lg:flex-row">
        
        {/* Lado Esquerdo (Desktop) / Embaixo (Mobile) - Textos da Escola */}
        <div className="relative w-full lg:w-1/2 h-auto py-8 lg:py-0 lg:h-full flex items-center justify-center bg-black z-10">
          <div className="text-center px-8 md:px-16 lg:px-20">
            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white font-bold mb-4 leading-tight">
              Ubiratan School
            </h1>
            <div className="w-24 h-1 bg-white mx-auto mb-6"></div>
            <p className="text-gray-300 text-sm md:text-base lg:text-lg max-w-md mx-auto italic font-light leading-relaxed mb-6 lg:mb-8">
              "Do zero ao avançado com metodologia prática e foco no mercado real"
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="#school-about" 
                className="px-8 py-3 bg-white text-black font-medium rounded-full hover:bg-gray-200 transition-colors duration-300"
                data-testid="school-hero-cta"
              >
                Conheça o Curso
              </a>
              <a 
                href="https://wa.me/5511934633755" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-8 py-3 border-2 border-white text-white font-medium rounded-full hover:bg-white hover:text-black transition-colors duration-300"
                data-testid="school-hero-whatsapp"
              >
                Fale Conosco
              </a>
            </div>
          </div>
        </div>

        {/* Lado Direito (Desktop) / Em Cima (Mobile) - Carrossel de trabalhos dos alunos */}
        <div className="relative w-full lg:w-1/2 h-[60vh] lg:h-full">
          {studentWorksImages.map((img, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentImage ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={img}
                alt={`Trabalho de aluno ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          {/* Gradiente para suavizar a transição */}
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-black to-transparent hidden lg:block" />
        </div>
      </div>
    </section>
  );
};

// Student Card Component with Carousel
const StudentCard = ({ name, images, currentImage }) => {
  return (
    <div className="bg-black rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 border border-white/20">
      <div className="aspect-square overflow-hidden relative">
        {images.map((img, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentImage ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <img
              src={img}
              alt={`Trabalho de ${name}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>
      <div className="p-6 text-center bg-white">
        <h3 className="font-serif text-xl text-black mb-2">{name}</h3>
        <p className="text-gray-600 text-sm">Resultado após o curso</p>
      </div>
    </div>
  );
};

// School Student Results Section
const SchoolResults = () => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setTick(prev => prev + 1);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const aluno1Images = [
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/juuqm99t_ChatGPT%20Image%2022%20de%20jan.%20de%202026%2C%2005_44_50.png",
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/w9mxp982_ChatGPT%20Image%2022%20de%20jan.%20de%202026%2C%2005_41_27.png",
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/lnmocvve_ChatGPT%20Image%2022%20de%20jan.%20de%202026%2C%2005_40_18.png"
  ];
  
  const aluno2Images = [
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/m4jh4rt0_2.jpeg",
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/2h6fo7uo_3.jpeg",
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/5lkegrd9_4.jpeg"
  ];
  
  const aluno3Images = [
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/2p8i12nk_11.jpeg",
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/pz9a8wlp_111.jpeg",
    "https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/c7m6cp80_22222222.jpeg"
  ];

  return (
    <section className="py-16 md:py-24 bg-black">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Resultados dos Alunos
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Veja o que nossos alunos conquistaram após a formação
          </p>
        </div>

        {/* Results Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          <StudentCard name="Aluno 1" images={aluno1Images} currentImage={tick % 3} />
          <StudentCard name="Aluno 2" images={aluno2Images} currentImage={tick % 3} />
          <StudentCard name="Aluno 3" images={aluno3Images} currentImage={tick % 3} />
        </div>
      </div>
    </section>
  );
};

// School About Section
const SchoolAbout = () => {
  return (
    <section id="school-about" className="py-16 md:py-24 bg-black">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative">
            <div className="aspect-[4/5] overflow-hidden rounded-lg shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_dual-theme-site/artifacts/fy2g8zbb_ChatGPT%20Image%2022%20de%20jan.%20de%202026%2C%2005_24_18.png"
                alt="Ubiratan School"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Content */}
          <div className="text-center lg:text-left">
            <span className="text-gray-500 uppercase tracking-widest text-sm mb-4 block">
              Sobre a Escola
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-8">
              Ubiratan School
            </h2>
            <div className="space-y-6 text-gray-400">
              <p className="leading-relaxed">
                A <strong className="text-white">Ubiratan School</strong> é referência em formação profissional de tatuadores. 
                Com mais de uma década de experiência no mercado e mais de 30 prêmios em convenções nacionais 
                e internacionais, oferecemos uma metodologia única e prática.
              </p>
              <p className="leading-relaxed">
                Nossa abordagem vai além da técnica: preparamos você para o mercado real, 
                com acompanhamento personalizado, desde os fundamentos até técnicas avançadas 
                de realismo, aquarela, fine line e muito mais.
              </p>
              <p className="leading-relaxed">
                <strong className="text-white">Diferenciais:</strong> Turmas reduzidas, material incluso, 
                certificação reconhecida, suporte pós-curso e networking com profissionais da área.
              </p>
            </div>
            <div className="mt-10">
              <Button
                onClick={() => window.open(`https://api.whatsapp.com/send?phone=5511934633755&text=${encodeURIComponent("Olá! Gostaria de saber mais sobre o curso da Ubiratan School.")}`, '_blank')}
                className="bg-white text-black hover:bg-gray-200 px-8 py-6 text-sm uppercase tracking-widest rounded-none"
              >
                Quero Me Inscrever
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// School Location Section
const SchoolLocation = () => {
  return (
    <section className="py-16 md:py-24 bg-black">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Localização
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Venha nos conhecer pessoalmente
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Address Info */}
          <div className="space-y-8 text-center lg:text-left">
            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-4">
              <div className="p-3 bg-white text-black rounded-lg">
                <MapPin size={24} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-white mb-2">Endereço</h3>
                <p className="text-gray-400">{locationData.address}</p>
                <p className="text-gray-400">{locationData.city}</p>
                <p className="text-gray-400">{locationData.postalCode}</p>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-4">
              <div className="p-3 bg-white text-black rounded-lg">
                <Phone size={24} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-white mb-2">Telefone</h3>
                <p className="text-gray-400">(11) 93463-3755</p>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-4">
              <div className="p-3 bg-white text-black rounded-lg">
                <Mail size={24} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-white mb-2">E-mail</h3>
                <p className="text-gray-400">Ubiratanschool@gmail.com</p>
              </div>
            </div>

            <div className="flex justify-center lg:justify-start">
              <Button
                onClick={() => window.open(`https://www.google.com/maps/search/${encodeURIComponent(locationData.fullAddress)}`, '_blank')}
                className="bg-white text-black hover:bg-gray-200 px-8 py-6 text-sm uppercase tracking-widest rounded-none mt-4"
              >
                <ExternalLink size={18} className="mr-2" />
                Abrir no Google Maps
              </Button>
            </div>
          </div>

          {/* Map */}
          <div className="h-[400px] rounded-lg overflow-hidden shadow-xl border border-gray-700">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3657.2!2d-46.5547!3d-23.5614!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94ce5fb2c15797a7%3A0x8a1234567890!2sR.%20Prof.%20Jo%C3%A3o%20de%20Oliveira%20Torres%2C%20405%20-%20Jardim%20An%C3%A1lia%20Franco%2C%20S%C3%A3o%20Paulo%20-%20SP%2C%2003337-010!5e0!3m2!1spt-BR!2sbr!4v1700000000000!5m2!1spt-BR!2sbr"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localização da Escola"
              className="w-full h-full"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// School Footer
const SchoolFooter = () => {
  return (
    <footer className="py-12 md:py-16 bg-black border-t border-gray-800">
      <div className="container-custom">
        <div className="grid md:grid-cols-3 gap-12 md:gap-8">
          {/* Logo & Description */}
          <div className="text-center md:text-left">
            <h3 className="font-serif text-2xl md:text-3xl text-white mb-4">Ubiratan School</h3>
            <p className="text-gray-400 mb-6">
              Formando tatuadores profissionais com excelência e dedicação desde 2008.
            </p>
            {/* Social Links */}
            <div className="flex gap-4 justify-center md:justify-start">
              <a
                href="https://www.instagram.com/ubiratan_school/"
                target="_blank"
                rel="noopener"
                className="p-3 bg-white text-black hover:bg-gray-200 transition-colors rounded-lg"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://www.facebook.com/UbiratanTattooStudio"
                target="_blank"
                rel="noopener"
                className="p-3 bg-white text-black hover:bg-gray-200 transition-colors rounded-lg"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://api.whatsapp.com/send?phone=5511934633755"
                target="_blank"
                rel="noopener"
                className="p-3 bg-white text-black hover:bg-gray-200 transition-colors rounded-lg"
                aria-label="WhatsApp"
              >
                <MessageCircle size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="text-center md:text-left">
            <h4 className="font-serif text-lg text-white mb-4">Links Rápidos</h4>
            <nav className="flex flex-col gap-3">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                Sobre o Curso
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                Resultados dos Alunos
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                Localização
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                Contato
              </a>
            </nav>
          </div>

          {/* Contact Info */}
          <div className="text-center md:text-left">
            <h4 className="font-serif text-lg text-white mb-4">Contato</h4>
            <div className="space-y-3">
              <p className="text-gray-400 flex items-center gap-2 justify-center md:justify-start">
                <Phone size={16} />
                (11) 93463-3755
              </p>
              <p className="text-gray-400 flex items-center gap-2 justify-center md:justify-start">
                <Mail size={16} />
                Ubiratanschool@gmail.com
              </p>
              <p className="text-gray-400 flex items-center gap-2 justify-center md:justify-start">
                <MapPin size={16} />
                {locationData.city}
              </p>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-12 pt-8 border-t border-gray-800 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Ubiratan School – Todos os direitos reservados
          </p>
        </div>
      </div>
    </footer>
  );
};

// Complete School Section Component
const UbiratanSchoolSection = () => {
  return (
    <div className="min-h-screen bg-black">
      <SchoolHero />
      <SchoolResults />
      <SchoolAbout />
      <SchoolLocation />
      <SchoolFooter />
    </div>
  );
};

// Main Landing Page Component
const LandingPage = ({ isSchoolRoute = false }) => {
  const [isSchoolMode, setIsSchoolMode] = useState(isSchoolRoute);

  // Sync state with route on mount
  useEffect(() => {
    setIsSchoolMode(isSchoolRoute);
  }, [isSchoolRoute]);

  return (
    <div className={`min-h-screen ${isSchoolMode ? 'bg-white' : 'bg-black'}`}>
      <Header isSchoolMode={isSchoolMode} setIsSchoolMode={setIsSchoolMode} />
      
      {/* Dark Portfolio Mode */}
      {!isSchoolMode && (
        <>
          <main>
            <HeroSection />
            <TattooCategories />
            <StylesCarousel />
            <TattooSchool />
            <AboutSection />
            <LocationSection />
            <Tour360Section />
          </main>
          <Footer />
        </>
      )}

      {/* School Mode */}
      {isSchoolMode && (
        <UbiratanSchoolSection />
      )}

      <FloatingWhatsApp isSchoolMode={isSchoolMode} />
    </div>
  );
};

export default LandingPage;
